/**
 * Wallet-portrait review queue (Founder Build 34).
 *
 * VeraLok-internal reviewers see every citizen-submitted portrait,
 * open the image alongside the vlk_id + citizen name, and decide:
 *   • Approve                → portrait becomes VeraLok-validated
 *   • Reject                 → citizen must upload a new photo
 *   • Request replacement    → citizen must upload a clearer photo
 *
 * A submitted photo is NEVER shown as verified on the Wallet card
 * unless an operator here approves it.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATE_STYLES = {
  PENDING_REVIEW: { color: "#B45309", label: "Pending Review" },
  APPROVED: { color: "#059669", label: "Approved" },
  REJECTED: { color: "#DC2626", label: "Rejected" },
  REPLACEMENT_REQUESTED: { color: "#D97706", label: "Replacement Requested" },
  SUPERSEDED: { color: "#6B7280", label: "Superseded" },
};

const FILTERS = ["PENDING_REVIEW", "APPROVED", "REJECTED", "REPLACEMENT_REQUESTED", "ALL"];

export default function WalletPortraits() {
  const [state, setState] = useState("PENDING_REVIEW");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/wallet/portraits?state=${state}`);
      setItems(r.data?.items ?? []);
    } catch (e) {
      toast.error("Could not load portrait queue.");
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/wallet/portraits/${selected.portrait_id}/decision`, {
        action,
        note: note.trim() || null,
      });
      toast.success(`Portrait ${action}.`);
      setSelected(null);
      setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Decision failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="wallet-portraits-page" className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Wallet Portraits</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Citizen-submitted photos awaiting VeraLok Admin review.
          </p>
        </div>
        <button
          data-testid="portraits-refresh"
          className="text-sm px-3 py-1.5 rounded border border-border hover:bg-muted"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`portraits-filter-${f.toLowerCase()}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              state === f
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent border-border text-muted-foreground hover:bg-muted"
            }`}
            onClick={() => setState(f)}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && !loading && (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded">
            No portraits in this state.
          </div>
        )}
        {items.map((p) => (
          <button
            key={p.portrait_id}
            data-testid={`portrait-card-${p.portrait_id}`}
            onClick={() => { setSelected(p); setNote(""); }}
            className="text-left p-3 rounded border border-border hover:bg-muted flex gap-3"
          >
            <PortraitThumb id={p.portrait_id} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate">
                  {p.citizen_full_name || "—"}
                </span>
                <StatePill state={p.state} />
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">
                {formatVlkId(p.vlk_id)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Submitted {formatTime(p.submitted_at)}
              </div>
              {p.reviewer_email && (
                <div className="text-xs text-muted-foreground mt-1">
                  Reviewed by {p.reviewer_email} · {formatTime(p.reviewed_at)}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <ReviewSheet
          portrait={selected}
          note={note}
          setNote={setNote}
          busy={busy}
          onClose={() => { setSelected(null); setNote(""); }}
          onDecide={decide}
        />
      )}
    </div>
  );
}

function PortraitThumb({ id }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/wallet/portraits/${id}/image`, { responseType: "blob" });
        if (cancelled) return;
        setBlobUrl(URL.createObjectURL(r.data));
      } catch (_) {/* keep placeholder */}
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <div className="w-16 h-16 rounded-full bg-muted overflow-hidden flex-shrink-0 border border-border">
      {blobUrl ? (
        <img src={blobUrl} alt="Portrait" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">…</div>
      )}
    </div>
  );
}

function StatePill({ state }) {
  const meta = STATE_STYLES[state] || { color: "#6B7280", label: state };
  return (
    <span
      data-testid="portrait-state-pill"
      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold"
      style={{
        color: meta.color,
        border: `1px solid ${meta.color}`,
        background: `${meta.color}15`,
      }}
    >
      {meta.label}
    </span>
  );
}

function ReviewSheet({ portrait, note, setNote, busy, onClose, onDecide }) {
  const canDecide = portrait.state === "PENDING_REVIEW";
  return (
    <div
      data-testid="portrait-review-modal"
      className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="max-w-lg w-full bg-card text-card-foreground rounded-lg p-5 space-y-4 border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Review portrait</h2>
          <button
            data-testid="portrait-modal-close"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="flex gap-4">
          <FullImage id={portrait.portrait_id} />
          <div className="flex-1 space-y-1 text-sm">
            <div className="font-semibold">{portrait.citizen_full_name || "—"}</div>
            <div className="font-mono text-xs text-muted-foreground">
              {formatVlkId(portrait.vlk_id)}
            </div>
            <div className="text-xs text-muted-foreground">
              State: <StatePill state={portrait.state} />
            </div>
            <div className="text-xs text-muted-foreground">
              Submitted {formatTime(portrait.submitted_at)}
            </div>
            {portrait.reviewer_email && (
              <div className="text-xs text-muted-foreground">
                Decided by {portrait.reviewer_email}
                <br />
                {formatTime(portrait.reviewed_at)}
              </div>
            )}
            {portrait.decision_note && (
              <div className="text-xs bg-muted rounded p-2 mt-1">
                <span className="font-semibold">Note:</span> {portrait.decision_note}
              </div>
            )}
          </div>
        </div>
        {canDecide ? (
          <>
            <textarea
              data-testid="portrait-review-note"
              className="w-full text-sm rounded border border-border bg-background p-2 h-20"
              placeholder="Optional note for the citizen and audit record."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                data-testid="portrait-action-reject"
                className="text-sm px-3 py-2 rounded border border-red-500 text-red-600 hover:bg-red-500/10"
                onClick={() => onDecide("reject")}
                disabled={busy}
              >
                Reject
              </button>
              <button
                data-testid="portrait-action-request-replacement"
                className="text-sm px-3 py-2 rounded border border-amber-500 text-amber-600 hover:bg-amber-500/10"
                onClick={() => onDecide("request_replacement")}
                disabled={busy}
              >
                Request replacement
              </button>
              <button
                data-testid="portrait-action-approve"
                className="text-sm px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => onDecide("approve")}
                disabled={busy}
              >
                Approve
              </button>
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground">
            This portrait is terminal; no further decisions can be made.
          </div>
        )}
      </div>
    </div>
  );
}

function FullImage({ id }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/wallet/portraits/${id}/image`, { responseType: "blob" });
        if (!cancelled) setBlobUrl(URL.createObjectURL(r.data));
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <div className="w-40 h-40 rounded bg-muted overflow-hidden flex-shrink-0 border border-border">
      {blobUrl ? (
        <img src={blobUrl} alt="Portrait" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
          Loading…
        </div>
      )}
    </div>
  );
}

function formatVlkId(id) {
  if (!id) return "—";
  const cleaned = String(id).replace(/[^0-9]/g, "");
  if (cleaned.length < 6) return id;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}
